import React from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Text,
} from '@fluentui/react-components';
import {
  ErrorCircle24Regular,
  Warning24Regular,
  Info24Regular,
} from '@fluentui/react-icons';

export type ErrorSeverity = 'error' | 'warning' | 'info';

interface ErrorModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  severity?: ErrorSeverity;
  details?: string;
  onRetry?: () => void;
  onClearCache?: () => void; // NEW: Cache clearing action
}

export const ErrorModal: React.FC<ErrorModalProps> = ({
  open,
  onClose,
  title = 'Error',
  message,
  severity = 'error',
  details,
  onRetry,
  onClearCache,
}) => {
  const getIcon = () => {
    switch (severity) {
      case 'warning':
        return <Warning24Regular style={{ color: '#f9a825' }} />;
      case 'info':
        return <Info24Regular style={{ color: '#0078d4' }} />;
      default:
        return <ErrorCircle24Regular style={{ color: '#d83b01' }} />;
    }
  };

  const getSeverityTitle = () => {
    switch (severity) {
      case 'warning':
        return title || 'Warning';
      case 'info':
        return title || 'Information';
      default:
        return title || 'Error';
    }
  };

  return (
    <Dialog open={open} onOpenChange={(event, data) => onClose()} data-testid="error-modal-dialog-root">
      <DialogSurface data-testid="error-modal">
        <DialogBody data-testid="error-modal-body">
          <DialogTitle data-testid="error-modal-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="error-modal-title-content">
              {getIcon()}
              {getSeverityTitle()}
            </div>
          </DialogTitle>
          <DialogContent data-testid="error-modal-content">
            <Text block style={{ marginBottom: details ? '12px' : 0 }} data-testid="error-modal-message">
              {message}
            </Text>
            {details && (
              <Text
                block
                data-testid="error-modal-details"
                size={200}
                style={{
                  padding: '8px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '200px',
                  overflowY: 'auto',
                }}
              >
                {details}
              </Text>
            )}
          </DialogContent>
          <DialogActions data-testid="error-modal-actions">
            {onClearCache && (
              <Button appearance="secondary" onClick={onClearCache} data-testid="error-modal-clear-cache">
                Clear Cache
              </Button>
            )}
            {onRetry && (
              <Button appearance="secondary" onClick={onRetry} data-testid="error-modal-retry">
                Retry
              </Button>
            )}
            <Button appearance="primary" onClick={onClose} data-testid="error-modal-ok">
              OK
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};